/**
 * Языки интерфейса. Исходные тексты в коде — по-русски (ключ словаря),
 * переводы лежат в словарях: ключ → [литовский, английский].
 * Литовский — язык по умолчанию.
 */

export const LANGS = ["lt", "ru", "en"] as const;
export type Lang = (typeof LANGS)[number];
export const DEFAULT_LANG: Lang = "lt";

export const LANG_NAMES: Record<Lang, string> = { lt: "Lietuvių", ru: "Русский", en: "English" };
export const LANG_SHORT: Record<Lang, string> = { lt: "LT", ru: "RU", en: "EN" };
/** Локаль для Intl (даты, числа, деньги) */
export const LOCALE: Record<Lang, string> = { lt: "lt-LT", ru: "ru-RU", en: "en-GB" };
/** Как назвать язык в инструкции для ИИ */
export const LANG_FOR_AI: Record<Lang, string> = {
  lt: "литовский (lietuvių kalba)",
  ru: "русский",
  en: "английский (English)",
};

export const isLang = (v: unknown): v is Lang => typeof v === "string" && (LANGS as readonly string[]).includes(v);

/** Язык из заголовка X-Lang / Accept-Language («lt», «ru-RU,ru;q=0.9» …) */
export function pickLang(...headers: (string | null | undefined)[]): Lang {
  for (const h of headers) {
    if (!h) continue;
    for (const part of h.split(",")) {
      const code = part.trim().slice(0, 2).toLowerCase();
      if (isLang(code)) return code;
    }
  }
  return DEFAULT_LANG;
}

/** Словарь: русский исходный текст → [lt, en]. Формы множественного числа разделяются «|». */
export type Dict = Record<string, readonly [lt: string, en: string]>;
export type Vars = Record<string, string | number | null | undefined>;

/**
 * Индекс формы множественного числа. Ключ во множественном числе — три русские формы
 * «{n} место|{n} места|{n} мест»; литовский перевод — три формы (vieta|vietos|vietų), английский — две.
 */
export function pluralIndex(lang: Lang, n: number): number {
  const cat = new Intl.PluralRules(LOCALE[lang]).select(n);
  if (lang === "en") return cat === "one" ? 0 : 1;
  if (lang === "lt") return cat === "one" ? 0 : cat === "few" || cat === "many" ? 1 : 2;
  // ru: one / few / many; дроби (other) — как few
  return cat === "one" ? 0 : cat === "few" || cat === "other" ? 1 : 2;
}

export function interpolate(text: string, vars?: Vars) {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (m, k: string) => (vars[k] === undefined || vars[k] === null ? m : String(vars[k])));
}

/** Текст на нужном языке (без подстановки). Нет перевода — русский исходник. */
export function lookup(dict: Dict, lang: Lang, key: string): string {
  if (lang === "ru") return key;
  const tr = dict[key];
  return (tr && tr[lang === "lt" ? 0 : 1]) || key;
}

export function makeTranslator(dict: Dict) {
  function tr(lang: Lang, key: string, vars?: Vars): string {
    return interpolate(lookup(dict, lang, key), vars);
  }
  function trn(lang: Lang, n: number, key: string, vars?: Vars): string {
    const forms = lookup(dict, lang, key).split("|");
    const form = forms[Math.min(pluralIndex(lang, n), forms.length - 1)];
    return interpolate(form, { n, ...vars });
  }
  return { tr, trn };
}

/** Слить несколько словарей (поздний перекрывает ранний) */
export function mergeDicts(...parts: Dict[]): Dict {
  return Object.assign({}, ...parts) as Dict;
}
